/**
 * Formula Editor Component
 *
 * Editor for Attack Potential and Damage Impact calculation formulas
 */

import React from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Input } from '../../../components/ui/Input';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/RadioGroup';
import CategoryPill from '../../../components/ui/CategoryPill/CategoryPill';
import styles from './FormulaEditor.module.css';

/** Strip the `_value` suffix and humanise the snake-case input key. */
const formatInputName = (input) =>
  input.replace(/_value$/, '').replace(/_/g, ' ');

/**
 * Return a human-readable representation of the formula expression
 * given the formula type, inputs, and (for weighted_average) weights.
 * Pure function — no backend calls; uses only the data already in the
 * configuration. Renders inside the `Current Formula:` row.
 */
const computeFormulaExpression = (formula) => {
  if (!formula || !Array.isArray(formula.inputs) || formula.inputs.length === 0) {
    return '—';
  }

  const names = formula.inputs.map(formatInputName);
  const weights = formula.weights || {};

  switch (formula.type) {
    case 'sum':
      return names.join(' + ');
    case 'max':
      return `MAX(${names.join(', ')})`;
    case 'min':
      return `MIN(${names.join(', ')})`;
    case 'average':
      return `(${names.join(' + ')}) / ${names.length}`;
    case 'weighted_average': {
      const terms = formula.inputs.map(
        (input, i) => `${names[i]} × ${weights[input] ?? 1}`,
      );
      const totalWeight = formula.inputs.reduce(
        (sum, input) => sum + (weights[input] ?? 1),
        0,
      );
      return `(${terms.join(' + ')}) / ${totalWeight}`;
    }
    default:
      return formula.type;
  }
};

const FORMULA_TYPES = [
  {
    value: 'sum',
    label: 'Sum',
    description: 'Add all input values together',
    example: 'Total = Value1 + Value2 + Value3 + ...'
  },
  {
    value: 'max',
    label: 'Maximum',
    description: 'Take the highest value among inputs',
    example: 'Total = MAX(Value1, Value2, Value3, ...)'
  },
  {
    value: 'min',
    label: 'Minimum',
    description: 'Take the lowest value among inputs',
    example: 'Total = MIN(Value1, Value2, Value3, ...)'
  },
  {
    value: 'average',
    label: 'Average',
    description: 'Calculate the mean of all input values',
    example: 'Total = AVG(Value1, Value2, Value3, ...)'
  },
  {
    value: 'weighted_average',
    label: 'Weighted Average',
    description: 'Calculate weighted average with custom weights',
    example: 'Total = (Value1×W1 + Value2×W2 + ...) / (W1 + W2 + ...)',
    requiresWeights: true
  }
];

export default function FormulaEditor() {
  const { config, updateConfiguration } = useConfiguration();

  if (!config?.configuration) {
    return <div className={styles.noData}>No formula configuration available</div>;
  }

  const attackPotentialFormula = config.configuration.attack_potential_formula;
  const damageImpactFormula = config.configuration.damage_impact_formula;

  const handleAttackFormulaChange = (newType) => {
    const updatedFormula = {
      ...attackPotentialFormula,
      type: newType
    };

    // If switching to WEIGHTED_AVERAGE, initialize default weights
    if (newType === 'weighted_average' && !attackPotentialFormula.weights) {
      updatedFormula.weights = {
        elapsed_time_value: 1.0,
        expertise_value: 1.0,
        knowledge_value: 1.0,
        window_value: 1.0,
        equipment_value: 1.0
      };
    }

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        attack_potential_formula: updatedFormula
      }
    };

    updateConfiguration(updatedConfig);
  };

  const handleDamageFormulaChange = (newType) => {
    const updatedFormula = {
      ...damageImpactFormula,
      type: newType
    };

    // If switching to WEIGHTED_AVERAGE, initialize default weights
    if (newType === 'weighted_average' && !damageImpactFormula.weights) {
      updatedFormula.weights = {
        safety: 1.0,
        financial: 1.0,
        operational: 1.0,
        privacy: 1.0
      };
    }

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        damage_impact_formula: updatedFormula
      }
    };

    updateConfiguration(updatedConfig);
  };

  const handleWeightChange = (formulaType, inputKey, newWeight) => {
    const weight = parseFloat(newWeight);
    if (isNaN(weight) || weight < 0 || weight > 10) {
      return;
    }

    const formulaKey = formulaType === 'attack' ? 'attack_potential_formula' : 'damage_impact_formula';
    const currentFormula = config.configuration[formulaKey];

    const updatedFormula = {
      ...currentFormula,
      weights: {
        ...currentFormula.weights,
        [inputKey]: weight
      }
    };

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        [formulaKey]: updatedFormula
      }
    };

    updateConfiguration(updatedConfig);
  };

  const renderWeightInputs = (formulaType, formula) => {
    if (formula.type !== 'weighted_average') return null;

    const inputs = formula.inputs || [];
    const weights = formula.weights || {};

    return (
      <div className={styles.weightsContainer}>
        <h4>Configure Weights</h4>
        <p className={styles.weightsDescription}>
          Assign weights (0-10) to each input. Higher weights give more importance to that factor.
        </p>
        <div className={styles.weightsGrid}>
          {inputs.map((input) => {
            const label = input.replace(/_value$/, '').replace(/_/g, ' ');
            const weight = weights[input] || 1.0;

            return (
              <div key={input} className={styles.weightRow}>
                <label className={styles.weightLabel}>{label}</label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  step={0.1}
                  value={weight}
                  onChange={(e) => handleWeightChange(formulaType, input, e.target.value)}
                  className={styles.weightInput}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Attack Potential Formula */}
      <div className={styles.formulaSection}>
        <div className={styles.formulaHeader}>
          <h3>Attack Potential Score Formula</h3>
        </div>

        <div className={styles.formulaCard}>
          <div className={styles.formulaInputs}>
            <label className={styles.label}>Current Formula:</label>
            <div className={styles.formulaExample}>
              Total = {computeFormulaExpression(attackPotentialFormula)}
            </div>
          </div>

          <div className={styles.formulaInputs}>
            <label className={styles.label}>Input Factors:</label>
            <div className={styles.inputsList}>
              {attackPotentialFormula?.inputs?.map((input) => (
                <CategoryPill key={input} className={styles.inputTag} label={formatInputName(input)} />
              ))}
            </div>
          </div>

          <div className={styles.formulaSelector}>
            <label className={styles.label}>Calculation Method:</label>
            <RadioGroup
              className={styles.formulaOptions}
              value={attackPotentialFormula?.type}
              onValueChange={handleAttackFormulaChange}
            >
              {FORMULA_TYPES.map((formula) => (
                <label
                  key={formula.value}
                  className={`${styles.formulaOption} ${attackPotentialFormula?.type === formula.value ? styles.selected : ''}`}
                >
                  <span className={styles.radioLabel}>
                    <RadioGroupItem value={formula.value} className={styles.radio} />
                    <div className={styles.formulaInfo}>
                      <div className={styles.formulaTitleRow}>
                        <span className={styles.formulaName}>{formula.label}</span>
                        <span className={styles.formulaDesc}>{formula.description}</span>
                      </div>
                      <div className={styles.formulaExample}>{formula.example}</div>
                    </div>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>

          {renderWeightInputs('attack', attackPotentialFormula)}
        </div>
      </div>

      {/* Damage Impact Formula */}
      <div className={styles.formulaSection}>
        <div className={styles.formulaHeader}>
          <h3>Damage Impact Rating Formula</h3>
        </div>

        <div className={styles.formulaCard}>
          <div className={styles.formulaInputs}>
            <label className={styles.label}>Current Formula:</label>
            <div className={styles.formulaExample}>
              Total = {computeFormulaExpression(damageImpactFormula)}
            </div>
          </div>

          <div className={styles.formulaInputs}>
            <label className={styles.label}>Impact Categories:</label>
            <div className={styles.inputsList}>
              {damageImpactFormula?.inputs?.map((input) => (
                <CategoryPill key={input} className={styles.inputTag} label={input} />
              ))}
            </div>
          </div>

          <div className={styles.formulaSelector}>
            <label className={styles.label}>Calculation Method:</label>
            <RadioGroup
              className={styles.formulaOptions}
              value={damageImpactFormula?.type}
              onValueChange={handleDamageFormulaChange}
            >
              {FORMULA_TYPES.map((formula) => (
                <label
                  key={formula.value}
                  className={`${styles.formulaOption} ${damageImpactFormula?.type === formula.value ? styles.selected : ''}`}
                >
                  <span className={styles.radioLabel}>
                    <RadioGroupItem value={formula.value} className={styles.radio} />
                    <div className={styles.formulaInfo}>
                      <div className={styles.formulaTitleRow}>
                        <span className={styles.formulaName}>{formula.label}</span>
                        <span className={styles.formulaDesc}>{formula.description}</span>
                      </div>
                      <div className={styles.formulaExample}>{formula.example}</div>
                    </div>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>

          {renderWeightInputs('damage', damageImpactFormula)}
        </div>
      </div>

      {/* Info Box */}
      <InstructionPanel title="Formula Guidelines">
        <ul>
          <li><strong>SUM (Default for Attack Potential):</strong> Follows ISO/SAE 21434 standard approach. Adds all factor values together to get total attack potential score.</li>
          <li><strong>MAX (Default for Damage Impact):</strong> Follows ISO/SAE 21434 standard approach. Takes the highest severity rating among all impact categories.</li>
          <li><strong>weighted_average:</strong> Allows prioritizing certain factors over others. Useful for organization-specific risk tolerance.</li>
          <li><strong>MIN:</strong> Conservative approach that considers the weakest link.</li>
          <li><strong>AVERAGE:</strong> Balanced approach giving equal weight to all factors.</li>
          <li>Changes take effect immediately for new analyses. Existing analyses are not affected.</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
